import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useMatch } from 'react-router';
import type { Room } from '../db/schema';
import { RoomDialog } from './RoomDialog';
import { DeleteRoomDialog } from './DeleteRoomDialog';

/**
 * The rail's `+`, the home grid's `+` card and the empty state all open the
 * same dialog, and a card's menu opens it on an existing room. One owner,
 * mounted once at the shell, rather than a copy of the state in each.
 */

type Open =
  | { kind: 'create' }
  | { kind: 'edit'; room: Room }
  | { kind: 'delete'; room: Room }
  | null;

type RoomDialogsApi = {
  openCreate: () => void;
  openEdit: (room: Room) => void;
  openDelete: (room: Room) => void;
};

const RoomDialogsContext = createContext<RoomDialogsApi | null>(null);

export function RoomDialogsProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<Open>(null);
  const navigate = useNavigate();
  const onBoard = useMatch('/room/:roomId');
  const onItem = useMatch('/room/:roomId/:itemId');
  const viewedRoomId = (onBoard ?? onItem)?.params.roomId;

  const api = useMemo<RoomDialogsApi>(
    () => ({
      openCreate: () => setOpen({ kind: 'create' }),
      openEdit: (room) => setOpen({ kind: 'edit', room }),
      openDelete: (room) => setOpen({ kind: 'delete', room }),
    }),
    [],
  );

  function close() {
    setOpen(null);
  }

  function afterDelete(room: Room) {
    close();
    // Deleting the room you're looking at would otherwise leave you on a
    // board that no longer exists.
    if (viewedRoomId === room.id) void navigate('/');
  }

  return (
    <RoomDialogsContext.Provider value={api}>
      {children}

      {/* Keyed so switching rooms rebuilds the form rather than reusing the
          previous room's field state. */}
      {open?.kind === 'create' && <RoomDialog key="create" onClose={close} />}
      {open?.kind === 'edit' && (
        <RoomDialog key={open.room.id} room={open.room} onClose={close} />
      )}
      {open?.kind === 'delete' && (
        <DeleteRoomDialog room={open.room} onClose={close} onDeleted={afterDelete} />
      )}
    </RoomDialogsContext.Provider>
  );
}

export function useRoomDialogs(): RoomDialogsApi {
  const api = useContext(RoomDialogsContext);
  if (api === null) {
    throw new Error('useRoomDialogs() called outside RoomDialogsProvider');
  }
  return api;
}
